import {noop} from "../lib/noop";
import {SinglePopover} from "../lib/bootstrapPopover";

export interface SvgViewRect {
	get x(): number

	get y(): number

	get centerX(): number

	get centerY(): number
}

export class SVGElementProxy implements SvgViewRect {
	private readonly _el: SVGElement;

	readonly width: number = 0
	readonly height: number = 0

	constructor(el: SVGElement) {
		this._el = el;
	}

	private _bounds() {
		const boundingRect = this._el.getBoundingClientRect();

		// @ts-ignore
		const ctm = this._el.getCTM() as SVGMatrix;

		// Create a point at the element's original coordinates (e.g., x=0, y=0 of its local space)
		// You might need to adjust based on the element's specific attributes (cx/cy for circles, x/y for rects, etc.)
		const p = this._el.ownerSVGElement?.createSVGPoint()
		if (p == null) throw new Error(`SVG cannot create point :_(`)
		p.x = 0; // Or element.x.baseVal.value if it has one
		p.y = 0; // Or element.y.baseVal.value
		const transformedPoint = p.matrixTransform(ctm);

		return {x: transformedPoint.x, y: transformedPoint.y, width: boundingRect.width, height: boundingRect.height}
	}

	get x(): number {
		return this._bounds().x;
	}

	get y(): number {
		return this._bounds().y;
	}

	get centerX() {
		const bound = this._bounds();
		return Math.round(bound.x + bound.width / 2);
	}

	get centerY() {
		const bound = this._bounds();
		return Math.round(bound.y + bound.height / 2);
	}
}

export class SvgView implements SvgViewRect {
	readonly view: SVGElement | null
	readonly viewDefs: SVGDefsElement[] = []

	readonly width: number = 0
	readonly height: number = 0
	x: number = 0
	y: number = 0

	private bindings: { key: string, consumer: (value: string) => void }[] | null = null;

	onClick: () => void = noop

	constructor(view: SVGElement | null) {
		this.view = view

		function getAttributeNumber(view: SVGElement, attr: string, remove: boolean = false) {
			const n = parseFloat(view.getAttribute(attr) as string)
			if (remove) view.removeAttribute(attr)
			return n;
		}

		if (view != null) {
			this.x = getAttributeNumber(view, 'x', true)
			this.y = getAttributeNumber(view, 'y', true)
			this.width = getAttributeNumber(view, 'width')
			this.height = getAttributeNumber(view, 'height')

			if (view.hasAttribute("transform")) {
				const match = /translate\((.+),(.+)\)/.exec(view.getAttribute("transform")!!);
				if (match) {
					this.x = parseFloat(match[1]);
					this.y = parseFloat(match[2]);
				}
			}

			view.addEventListener('click', () => this.onClick())
			view.style.cursor = 'pointer'
		}

		// Все defs надо перевести на корневой уровень. Иначе в firefox оно не заработает
		if (view != null) {
			view.querySelectorAll('defs').forEach(defsNode => {
				this.viewDefs.push(defsNode)
				defsNode.remove();
				view.ownerSVGElement?.prepend(defsNode);
			})
		}

		this.updateLocation();
	}

	dispose() {
		this.viewDefs.forEach(defsNode => {
			defsNode.remove()
		})
		this.view?.remove()
	}

	get centerX() {
		return Math.round(this.x + this.width / 2);
	}

	get centerY() {
		return Math.round(this.y + this.height / 2);
	}

	toX(x: number) {
		this.x = x
		this.updateLocation()
	}

	toCenterX(centerX: number) {
		const offset = this.centerX - this.x;
		this.x = centerX - offset;
		this.updateLocation()
	}

	toY(y: number) {
		this.y = y
		this.updateLocation()
	}

	toggleClass(className: string, on: boolean) {
		const classList = this.view?.classList
		if (!classList) return;

		if (on && !classList.contains(className)) {
			classList.add(className);
		}
		if (!on && classList.contains(className)) {
			classList.remove(className);
		}
	}

	bind(key: string, value: string) {
		this.viewDefs.forEach(defsNode => {
			if (defsNode.parentNode == null) {
				this.view?.ownerSVGElement?.prepend(defsNode);
			}
		})

		const bindings = this.bindLookup();
		bindings
			.filter(b => b.key == key)
			.forEach(b => b.consumer(value))
	}

	bindWith(key: string, consumer: (svg: SVGElement) => void) {
		this.view?.querySelectorAll<SVGElement>(`[data-bind=${key}]`).forEach(consumer);
	}

	private bindLookup(): { key: string, consumer: (text: string) => void }[] {
		if (this.bindings != null) return this.bindings
		this.bindings = [];

		if (!this.view) return []

		function walkChildren(parent: Element, visitor: (element: Element) => void) {
			visitor(parent)
			const children = parent.children;
			for (let i = 0; i < children.length; ++i) {
				const child = children.item(i);
				if (child) walkChildren(child, visitor)
			}
		}

		const bindElement = (parent: Element) => {
			walkChildren(parent, el => {
				const bindValue = el.getAttribute('data-bind');
				if (bindValue != null) {
					this.bindings?.push({key: bindValue, consumer: (value) => el.textContent = value})
				}

				el.getAttributeNames().forEach(attr => {
					if (/^data-bind$/.test(attr)) {
						const key = el.getAttribute(attr);
						if (key) this.bindings?.push({key, consumer: (value) => el.textContent = value})
					}
					const attributeTarget = /^data-bind-(.+)$/.exec(attr)
					if (attributeTarget) {
						const attribute = attributeTarget[1];
						const key = el.getAttribute(attr);
						if (attribute && key) this.bindings?.push({key, consumer: (value) => el.setAttribute(attribute, value)})
					}
				})
			})
		}

		bindElement(this.view);
		this.viewDefs.forEach(defsNode => bindElement(defsNode))

		return this.bindings;
	}

	updateLocation() {
		if (this.view != null) {
			this.view.setAttribute("transform", `translate(${this.x}, ${this.y})`)
		}
		SinglePopover.updateForElement(this.view)
	}
}